import React, { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Users, CalendarDays, Clock, AlertTriangle, Banknote, Landmark } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHead } from "@/components/hr/Shared";
import { HR, card, inp, thisMonth, downloadFile, errText } from "@/lib/hr";

const REPORTS = [
  { kind: "employees", title: "Employee list", desc: "Full master list incl. NRIC/FIN, contact, employment and work-pass details.", icon: Users },
  { kind: "leave", title: "Leave records", desc: "All leave for the selected month (or whole year if month is cleared).", icon: CalendarDays, period: true },
  { kind: "attendance", title: "Attendance", desc: "Clock in/out, hours and OT for the selected month.", icon: Clock, period: true },
  { kind: "document-expiry", title: "Document & pass expiry", desc: "Everything expired or expiring within the HR warning period.", icon: AlertTriangle },
  { kind: "compensation", title: "Compensation history", desc: "Every compensation record per employee with effective dates.", icon: Banknote },
  { kind: "iras", title: "IRAS basic pay summary", desc: "Per employee for a calendar year: months in service, annual basic pay, allowances and other compensation — from HR compensation history only.", icon: Landmark, year: true },
];

export default function HRReports() {
  const [month, setMonth] = useState(thisMonth());
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [busy, setBusy] = useState("");
  const go = async (r, format) => {
    setBusy(`${r.kind}-${format}`);
    try { await downloadFile(`${HR}/export/${r.kind}`, { format, ...(r.period ? (month ? { month } : { year }) : {}), ...(r.year ? { year } : {}) }, `hr-${r.kind}.${format}`); }
    catch (e) { toast.error(errText(e, "Export failed")); } finally { setBusy(""); }
  };
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4 pb-24 md:pb-6" data-testid="hr-reports">
        <PageHead title="Reports & export" sub="Excel or CSV. Exports contain HR data only — never passwords, tokens or data from other modules." />
        <div className={`${card} p-3 flex flex-wrap items-center gap-3 text-sm`}>
          <label className="flex items-center gap-2 text-xs text-slate-400">Month <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="rep-month" className={`${inp} h-9 w-[170px] [color-scheme:dark] font-mono-num`} /></label>
          <label className="flex items-center gap-2 text-xs text-slate-400">Year <Input type="number" min="2000" max="2100" value={year} onChange={(e) => setYear(e.target.value)} data-testid="rep-year" className={`${inp} h-9 w-[110px] font-mono-num`} /></label>
          <span className="text-xs text-slate-500">Clear the month to export a whole year for leave / attendance.</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {REPORTS.map((r) => (
            <div key={r.kind} className={`${card} p-4 flex flex-col gap-3`} data-testid={`rep-${r.kind}`}>
              <div className="flex items-start gap-3"><r.icon className="w-6 h-6 text-orange-300 shrink-0" /><div><div className="font-semibold">{r.title}</div><p className="text-xs text-slate-400 mt-0.5">{r.desc}</p></div></div>
              <div className="flex gap-2 mt-auto">
                <Button size="sm" onClick={() => go(r, "xlsx")} disabled={!!busy} data-testid={`rep-${r.kind}-xlsx`} className="h-8 bg-orange-500 hover:bg-orange-400 text-slate-950 font-bold"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> {busy === `${r.kind}-xlsx` ? "…" : "Excel"}</Button>
                <Button size="sm" variant="outline" onClick={() => go(r, "csv")} disabled={!!busy} data-testid={`rep-${r.kind}-csv`} className="h-8 border-slate-700 bg-transparent"><FileText className="w-3.5 h-3.5 mr-1" /> {busy === `${r.kind}-csv` ? "…" : "CSV"}</Button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
